import React from 'react'
import { Box, Typography, RadioGroup, Radio, FormControlLabel } from '@mui/material';

import { withStyles } from '@mui/styles'

import { radioInputStyles } from './radio-input.styles'

/**
 * @typedef {object} RadioOption
 * @property {string | number} key - El identificador único de la opción.
 * @property {string} value - El texto que se mostrará para la opción.
 */

/**
 * @typedef {object} RadioInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {boolean} [readOnly=false] - Si es `true`, muestra el valor seleccionado como texto estático.
 * @property {RadioOption[]} [values=[]] - Un array de opciones disponibles para seleccionar.
 * @property {string} [title] - El título que se muestra sobre el grupo de radio buttons.
 * @property {string | number} value - El valor actualmente seleccionado.
 * @property {string} name - El nombre del campo.
 * @property {function(React.ChangeEvent): void} onChange - La función callback que se ejecuta cuando la selección cambia.
 */

/**
 * Un componente de entrada de radio buttons que permite seleccionar una única opción de una lista.
 * 
 * Renderiza un grupo horizontal de radio buttons usando Material-UI. Incluye un modo de solo lectura
 * que muestra el valor seleccionado como texto. El texto en modo lectura se capitaliza automáticamente.
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [gender, setGender] = useState('male');
 * 
 *   const options = [
 *     { key: 'male', value: 'Masculino' },
 *     { key: 'female', value: 'Femenino' },
 *     { key: 'other', value: 'Otro' },
 *   ];
 * 
 *   const handleChange = (event) => {
 *     setGender(event.target.value);
 *   };
 * 
 *   return (
 *     <RadioInput
 *       title="Género"
 *       name="gender"
 *       values={options}
 *       value={gender}
 *       onChange={handleChange}
 *     />
 *   );
 * };
 * 
 * @param {RadioInputProps} props - Las props del componente RadioInput.
 */
export const RadioInput = withStyles(radioInputStyles)(
  ({ classes, readOnly = false, values = [], ...props }) => {
    const getValue = () => {
      let optionSelected
      let valueSelected = '--'
      if (props.value && values) {
        optionSelected = values.find((option) => option.key === props.value)
        valueSelected = optionSelected ? optionSelected.value : '--'
      }
      return valueSelected
    }
    return (
      <Box>
        {props.title ? (
          <Typography variant="subtitle1" gutterBottom>
            {props.title}
          </Typography>
        ) : null}
        {readOnly ? (
          <Typography variant="body1" color="textSecondary" style={{ textTransform: 'capitalize' }}>
            {getValue()}
          </Typography>
        ) : (
          <RadioGroup
            row
            value={props.value}
            onChange={props.onChange}
            style={{ paddingLeft: 10 }}
            name={props.name}
          >
            {values.map(({ key, value }) => {
              return (
                <FormControlLabel
                  key={key}
                  value={key}
                  name={props.name}
                  control={<Radio size="small" color="primary" />}
                  label={
                    <Typography variant="body1" color="textSecondary">
                      {value}
                    </Typography>
                  }
                />
              )
            })}
          </RadioGroup>
        )}
      </Box>
    )
  }
)
